import { useEffect, useState } from "react";

import {
	fetchJobylonLinkedJobs,
	JobylonLinkedJob,
} from "../api/assignments/jobylon-jobs";

/**
 * Fired on `window` after a Jobylon job is linked to an assignment, carrying
 * the assignment's id. Every view showing Jobylon marks refetches its map, and
 * a view holding that assignment can reload it — linking also writes the ad's
 * external URL and deadline onto the row, which an object fetched at creation
 * time does not have.
 *
 * The wizard fires it; the hook and Todo listen. Nothing else needs wiring.
 */
export const JOBYLON_LINK_CHANGED_EVENT = "jobylon-link-changed";

export interface JobylonLinkChangedDetail {
	assignmentId: number;
}

export const notifyJobylonLinkChanged = (assignmentId: number): void => {
	window.dispatchEvent(
		new CustomEvent<JobylonLinkChangedDetail>(JOBYLON_LINK_CHANGED_EVENT, {
			detail: { assignmentId },
		})
	);
};

/**
 * Each Jobylon-sourced role's Jobylon job, keyed by ODI assignment id.
 *
 * Empty for a customer without the integration, which is what keeps every
 * Jobylon mark and link off their screens without any caller having to ask
 * whether Jobylon is configured.
 *
 * Failure is deliberately quiet. Every caller uses this to decide a mark or a
 * link on a page about something else, so a Jobylon outage must not take that
 * page down — it just means no Jobylon affordances until the next load.
 */
export const useJobylonLinkedJobs = (): Map<number, JobylonLinkedJob> => {
	const [byAssignment, setByAssignment] = useState<Map<number, JobylonLinkedJob>>(
		new Map()
	);

	useEffect(() => {
		let controller = new AbortController();

		const load = () => {
			controller.abort();
			controller = new AbortController();
			fetchJobylonLinkedJobs(controller.signal)
				.then((jobs) =>
					setByAssignment(new Map(jobs.map((job) => [job.assignment, job])))
				)
				.catch((err: unknown) => {
					if (err instanceof DOMException && err.name === "AbortError")
						return;
					console.debug("Jobylon linked jobs unavailable", err);
				});
		};

		load();
		// A job linked while this view is open — the wizard just imported one —
		// must show up without a page reload.
		window.addEventListener(JOBYLON_LINK_CHANGED_EVENT, load);
		return () => {
			controller.abort();
			window.removeEventListener(JOBYLON_LINK_CHANGED_EVENT, load);
		};
	}, []);

	return byAssignment;
};
